import { AccessOverride, UserProfile } from '../models/user.model';

export type ClientAccessReason =
  | 'NOT_CLIENT'
  | 'OVERRIDE'
  | 'VERIFIED'
  | 'KYC_PENDING'
  | 'KYC_ACTION_REQUIRED'
  | 'QUIZ_PENDING'
  | 'QUIZ_ACTION_REQUIRED'
  | 'AML_PENDING'
  | 'AML_ACTION_REQUIRED';

export interface ClientAccessState {
  hasAccess: boolean;
  reason: ClientAccessReason;
  blockingRoute: '/client/kyc-questionnaire' | '/client/financial-quiz' | '/client/kyc-pending' | null;
  bannerTitle: string | null;
  bannerMessage: string | null;
  actionLabel: string | null;
}

export function accessOverrideIsActive(accessOverride?: AccessOverride | null): boolean {
  if (!accessOverride?.enabled) {
    return false;
  }

  if (!accessOverride.expiresAt) {
    return true;
  }

  return new Date(accessOverride.expiresAt).getTime() > Date.now();
}

export function getClientAccessState(userProfile: UserProfile | null | undefined): ClientAccessState {
  if (!userProfile || userProfile.role !== 'Client') {
    return {
      hasAccess: false,
      reason: 'NOT_CLIENT',
      blockingRoute: null,
      bannerTitle: null,
      bannerMessage: null,
      actionLabel: null,
    };
  }

  if (accessOverrideIsActive(userProfile.accessOverride)) {
    return {
      hasAccess: true,
      reason: 'OVERRIDE',
      blockingRoute: null,
      bannerTitle: 'Manual Access Override Active',
      bannerMessage: userProfile.accessOverride?.expiresAt
        ? `Access has been manually enabled until ${new Date(userProfile.accessOverride.expiresAt).toLocaleDateString('en-GB')}.`
        : 'Access has been manually enabled by the compliance team.',
      actionLabel: null,
    };
  }

  if (userProfile.kycStatus !== 'Verified') {
    if (userProfile.kycStatus === 'Pending') {
      return {
        hasAccess: false,
        reason: 'KYC_PENDING',
        blockingRoute: '/client/kyc-pending',
        bannerTitle: 'Verification Pending',
        bannerMessage: 'Your KYC submission is under review. Access will unlock after both KYC and AML checks are complete.',
        actionLabel: null,
      };
    }

    return {
      hasAccess: false,
      reason: 'KYC_ACTION_REQUIRED',
      blockingRoute: '/client/kyc-questionnaire',
      bannerTitle: 'Action Required: Complete Your Profile',
      bannerMessage: 'To unlock your account, complete or update your financial questionnaire and upload the required documents.',
      actionLabel: 'Continue Questionnaire',
    };
  }

  if (userProfile.amlStatus !== 'Verified') {
    return {
      hasAccess: false,
      reason: userProfile.amlStatus === 'Pending' ? 'AML_PENDING' : 'AML_ACTION_REQUIRED',
      blockingRoute: '/client/kyc-pending',
      bannerTitle: 'Compliance Review Pending',
      bannerMessage: userProfile.amlStatus === 'Pending'
        ? 'Your KYC is approved, but AML checks are still in progress. We will notify you once access is unlocked.'
        : 'Your account requires further compliance review. Please contact support if you have questions.',
      actionLabel: null,
    };
  }

  if (userProfile.financialQuizStatus !== 'Verified') {
    if (userProfile.financialQuizStatus === 'Pending') {
      return {
        hasAccess: false,
        reason: 'QUIZ_PENDING',
        blockingRoute: '/client/kyc-pending',
        bannerTitle: 'Financial Quiz Pending',
        bannerMessage: 'Your knowledge check has been submitted and is under review.',
        actionLabel: null,
      };
    }

    return {
      hasAccess: false,
      reason: 'QUIZ_ACTION_REQUIRED',
      blockingRoute: '/client/financial-quiz',
      bannerTitle: 'Action Required: Complete Financial Quiz',
      bannerMessage: 'Please complete the financial knowledge check before access is enabled.',
      actionLabel: 'Continue Quiz',
    };
  }

  return {
    hasAccess: true,
    reason: 'VERIFIED',
    blockingRoute: null,
    bannerTitle: null,
    bannerMessage: null,
    actionLabel: null,
  };
}
